import React from 'react';
import { Box, Tooltip, BoxProps, useTheme } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';

interface SkeletonWithTooltipProps extends BoxProps {
  tooltipText?: string;
  width?: number | string;
  height?: number | string;
}

const SkeletonWithTooltip: React.FC<SkeletonWithTooltipProps> = ({ 
  tooltipText,
  width = 120,
  height = 40,
  sx,
  ...boxProps 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const defaultTooltipText = t('common.purchasePlanToSeeValues');
  
  // Create a pattern of squares with different shades
  const generateSquarePattern = () => {
    const squares = [];
    const squareSize = 8;
    const cols = Math.ceil(Number(width) / squareSize);
    const rows = Math.ceil(Number(height) / squareSize);
    
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        const opacity = Math.random() * 0.3 + 0.1;
        squares.push({
          key: `${i}-${j}`,
          left: j * squareSize,
          top: i * squareSize,
          opacity: opacity
        });
      }
    }
    return squares;
  };
  
  const squares = generateSquarePattern();
  
  return (
    <Tooltip title={tooltipText || defaultTooltipText} arrow>
      <Box
        {...boxProps}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width,
          height,
          backgroundColor: isDarkMode 
            ? '#2a2a2a' 
            : '#e0e0e0',
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          ...sx
        }}
      >
        {/* Background pattern of squares */}
        {squares.map((square) => (
          <Box
            key={square.key}
            sx={{
              position: 'absolute',
              left: square.left,
              top: square.top,
              width: 8,
              height: 8,
              backgroundColor: isDarkMode 
                ? `rgba(255, 255, 255, ${square.opacity})` 
                : `rgba(0, 0, 0, ${square.opacity})`,
            }}
          />
        ))}
        
        <LockIcon 
          sx={{ 
            color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
            fontSize: '1.2rem',
            position: 'relative',
            zIndex: 1,
          }} 
        />
      </Box>
    </Tooltip>
  );
};

export default SkeletonWithTooltip;